import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  writeBatch
} from "firebase/firestore";
import * as XLSX from "xlsx";

function AchievementManager() {
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [lessons, setLessons] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState("");
  const [achievements, setAchievements] = useState([]);
  const [filteredAchievements, setFilteredAchievements] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  useEffect(() => {
    const fetchClasses = async () => {
      const snapshot = await getDocs(collection(db, "classes"));
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClasses(list);
    };
    fetchClasses();
  }, []);

  useEffect(() => {
    const fetchLessons = async () => {
      if (!selectedClassId) return;
      const q = query(
        collection(db, "lessons"),
        where("classId", "==", selectedClassId)
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLessons(list);
      setSelectedLessonId("");
    };
    fetchLessons();
  }, [selectedClassId]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      const newAchievements = parsed.slice(1).map(row => ({
        week: row[0],
        date: row[1],
        text: row[2],
      }));

      setAchievements(newAchievements);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSaveAll = async () => {
    if (!selectedClassId || !selectedLessonId) {
      alert("Lütfen sınıf ve ders seçiniz.");
      return;
    }

    try {
      const batchSaves = achievements.map(ach =>
        addDoc(collection(db, "achievements"), {
          classId: selectedClassId,
          lessonId: selectedLessonId,
          week: ach.week,
          date: ach.date,
          text: ach.text,
          createdAt: serverTimestamp(),
        })
      );

      await Promise.all(batchSaves);
      alert("🎉 Kazanımlar başarıyla kaydedildi!");
      setAchievements([]);
    } catch (error) {
      console.error("Kayıt hatası:", error);
      alert("❌ Bir hata oluştu, tekrar deneyin.");
    }
  };

  useEffect(() => {
    const fetchAchievements = async () => {
      if (!selectedClassId || !selectedLessonId) {
        setFilteredAchievements([]);
        return;
      }

      const q = query(
        collection(db, "achievements"),
        where("classId", "==", selectedClassId),
        where("lessonId", "==", selectedLessonId)
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFilteredAchievements(list);
    };

    fetchAchievements();
  }, [selectedClassId, selectedLessonId]);

  const handleEdit = (id, item) => {
    setEditingId(id);
    setEditValues(item);
  };

  const handleEditChange = (field, value) => {
    setEditValues({ ...editValues, [field]: value });
  };

  const handleEditSave = async (id) => {
    const docRef = doc(db, "achievements", id);
    await updateDoc(docRef, editValues);
    setEditingId(null);
    setEditValues({});
    alert("✅ Güncellendi");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kazanımı silmek istediğinizden emin misiniz?")) return;
    await deleteDoc(doc(db, "achievements", id));
    setFilteredAchievements(filteredAchievements.filter(item => item.id !== id));
  };

  const handleDeleteAll = async () => {
    if (!selectedClassId || !selectedLessonId) return;
    if (!window.confirm("Bu derse ait TÜM kazanımları silmek istediğinize emin misiniz?")) return;

    const q = query(
      collection(db, "achievements"),
      where("classId", "==", selectedClassId),
      where("lessonId", "==", selectedLessonId)
    );
    const snapshot = await getDocs(q);
    const batch = writeBatch(db);

    snapshot.docs.forEach(docSnap => {
      batch.delete(doc(db, "achievements", docSnap.id));
    });

    await batch.commit();
    setFilteredAchievements([]);
    alert("❌ Tüm kazanımlar silindi.");
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredAchievements.map(({ week, date, text }) => ({ week, date, text })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Kazanımlar");
    XLSX.writeFile(wb, "kazanımlar.xlsx");
  };

  return (
    <div style={{ maxWidth: "800px", margin: "auto", padding: "20px" }}>
      <h2 style={{ textAlign: "center", marginBottom: "30px" }}>🎯 Kazanım Ekle</h2>

      <div style={{ marginBottom: "20px" }}>
        <label style={labelStyle}>Sınıf Seç:</label>
        <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} style={selectStyle}>
          <option value="">-- Sınıf Seçiniz --</option>
          {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.name}</option>)}
        </select>
      </div>

      {selectedClassId && (
        <div style={{ marginBottom: "30px" }}>
          <label style={labelStyle}>Ders Seç:</label>
          <select value={selectedLessonId} onChange={(e) => setSelectedLessonId(e.target.value)} style={selectStyle}>
            <option value="">-- Ders Seçiniz --</option>
            {lessons.map(lesson => <option key={lesson.id} value={lesson.id}>{lesson.name}</option>)}
          </select>
        </div>
      )}

      <div style={{ marginBottom: "30px" }}>
        <label style={labelStyle}>Excel'den Kazanım Yükle:</label>
        <input type="file" accept=".xlsx, .xls" onChange={handleFileUpload} />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {achievements.map((ach, idx) => (
          <div key={idx} style={cardStyle}>
            <p><strong>📅 Hafta:</strong> {ach.week}</p>
            <p><strong>🗓️ Tarih:</strong> {ach.date}</p>
            <p><strong>📌 Kazanım:</strong> {ach.text}</p>
          </div>
        ))}
      </div>

      {achievements.length > 0 && (
        <div style={{ marginTop: "30px", textAlign: "center" }}>
          <button onClick={handleSaveAll} style={{ padding: "12px 24px", backgroundColor: "#28a745", color: "#fff", fontWeight: "bold", border: "none", borderRadius: "10px", cursor: "pointer", boxShadow: "0 3px 6px rgba(0,0,0,0.1)" }}>
            ✅ Tüm Kazanımları Kaydet
          </button>
        </div>
      )}

      {filteredAchievements.length > 0 && (
        <div style={{ marginTop: "50px" }}>
          <h3 style={{ marginBottom: "20px" }}>📋 Bu Sınıf ve Derse Ait Kayıtlı Kazanımlar</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
            {filteredAchievements.map((ach) => (
              <div key={ach.id} style={cardStyle}>
                {editingId === ach.id ? (
                  <>
                    <input value={editValues.week} onChange={(e) => handleEditChange("week", e.target.value)} />
                    <input value={editValues.date} onChange={(e) => handleEditChange("date", e.target.value)} />
                    <textarea value={editValues.text} onChange={(e) => handleEditChange("text", e.target.value)} />
                    <button onClick={() => handleEditSave(ach.id)}>Kaydet</button>
                  </>
                ) : (
                  <>
                    <p><strong>📅 Hafta:</strong> {ach.week}</p>
                    <p><strong>🗓️ Tarih:</strong> {ach.date}</p>
                    <p><strong>📌 Kazanım:</strong> {ach.text}</p>
                    <button onClick={() => handleEdit(ach.id, ach)}>Düzenle</button>
                    <button onClick={() => handleDelete(ach.id)}>Sil</button>
                  </>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: "30px", textAlign: "center" }}>
            <button onClick={exportToExcel} style={{ marginRight: "15px" }}>📊 Excel'e Aktar</button>
            <button onClick={handleDeleteAll} style={{ backgroundColor: "#dc3545", color: "white", padding: "10px 20px", border: "none", borderRadius: "8px", cursor: "pointer" }}>🗑️ Tümünü Sil</button>
          </div>
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  width: "100%",
  padding: "10px",
  borderRadius: "10px",
  border: "1px solid #ccc",
  fontSize: "16px",
  marginTop: "5px",
};

const labelStyle = {
  fontWeight: "bold",
  display: "block",
  marginBottom: "5px",
};

const cardStyle = {
  width: "220px",
  backgroundColor: "#f8f9fa",
  border: "1px solid #ccc",
  borderRadius: "12px",
  padding: "15px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
};

export default AchievementManager;
